import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Popup} from 'devextreme-react/popup';
import {Form, SimpleItem, RequiredRule} from 'devextreme-react/form';
import Button from 'devextreme-react/button';
import notify from 'devextreme/ui/notify';
import {apiRequest} from 'api/async';
import {vars} from 'utils/variables';

const {APP_CODES: {SUCCESS}} = vars;

const MASK = '********';
const TIMER_SECONDS = 15;

const SENSITIVE_FIELDS = ['Token', 'CryptoNotifySignKey', 'PublicKey'];

const TOKEN_LENGTH = 90;
const TOKEN_FIRST_CHARSET = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
const TOKEN_CHARSET = `${TOKEN_FIRST_CHARSET}/\\|-_`;

const isMasked = (value) => value === MASK || value === '***';
const toBool = (value) => value === true || value === 1 || value === '1';

const POPUP_TITLES = {
  view: 'Просмотр мерчанта',
  edit: 'Редактирование мерчанта',
  add: 'Добавление мерчанта',
};

const SENSITIVE_TIMER_BUTTON_NAME = 'revealTimer';
const SENSITIVE_TOGGLE_BUTTON_NAME = 'toggleReveal';
const TOKEN_GENERATE_BUTTON_NAME = 'generateToken';

const randomInt = (maxExclusive) => {
  if (!maxExclusive || maxExclusive <= 0) return 0;

  const cryptoObj = (typeof window !== 'undefined' && window.crypto) ? window.crypto : null;
  if (cryptoObj && cryptoObj.getRandomValues) {
    const uint32 = new Uint32Array(1);
    cryptoObj.getRandomValues(uint32);
    return uint32[0] % maxExclusive;
  }

  return Math.floor(Math.random() * maxExclusive);
};

const generateTokenValue = () => {
  let result = TOKEN_FIRST_CHARSET.charAt(randomInt(TOKEN_FIRST_CHARSET.length));
  for (let i = 1; i < TOKEN_LENGTH; i++) {
    result += TOKEN_CHARSET.charAt(randomInt(TOKEN_CHARSET.length));
  }
  return result;
};

const MerchantPopup = ({mode, data, haveEnoughRight, onClose, onSuccess}) => {
  const formRef = useRef(null);

  // Стан форми
  const [formData, setFormData] = useState(() => {
    if (mode === 'add') {
      return {
        ID: '',
        Name: '',
        Token: '',
        CryptoNotifySignKey: '',
        NotifyUrl: '',
        PublicKey: '',
        IncomeRedirectUrl: '',
        IsGatewayCardPaymentAllow: false,
        GatewayCardPaymentSystemTypeID: null,
        CryptoNotifyUrl: '',
        IsPaymentCascade: false,
      };
    }
    if (!data) return {};
    return {
      ...data,
      IsGatewayCardPaymentAllow: toBool(data.IsGatewayCardPaymentAllow),
      IsPaymentCascade: toBool(data.IsPaymentCascade),
    };
  });

  // Трекінг зміненних полів (для Edit — тільки відправляти змінені)
  const changedFields = useRef({});

  const handleFieldChange = useCallback((field, value) => {
    changedFields.current[field] = value;
  }, []);

  const canReveal = haveEnoughRight;

  const isReadOnly = mode === 'view';

  const handleSave = async () => {
    if (mode === 'view') {
      onClose();
      return;
    }

    if (mode === 'add') {
      // Валідація обов'язкових полів
      const isValid = formRef.current?.instance?.().validate?.().isValid;
      if (!isValid) return;

      const currentFormData = formRef.current?.instance?.().option?.('formData') || formData || {};

      // Не відправляємо поля з MASK значенням
      const payload = {...currentFormData};
      SENSITIVE_FIELDS.forEach((f) => {
        if (isMasked(payload[f])) delete payload[f];
      });
      // IsGatewayCardPaymentAllow: 0/1
      payload.IsGatewayCardPaymentAllow = payload.IsGatewayCardPaymentAllow ? 1 : 0;
      payload.IsPaymentCascade = payload.IsPaymentCascade ? 1 : 0;

      try {
        const response = await apiRequest({
          operation: 'Merchant/Ins',
          data: {Params: payload},
        });
        if (response?.data?.ResponseCode === SUCCESS) {
          notify('Мерчант добавлен', 'success', 2000);
          onClose();
          onSuccess();
        } else {
          notify(response?.data?.ResponseText || 'Ошибка', 'error', 3000);
        }
      } catch (e) {
        notify('Ошибка сети', 'error', 3000);
      }
      return;
    }

    if (mode === 'edit') {
      const changed = changedFields.current;
      if (!Object.keys(changed).length) {
        onClose();
        return;
      }

      const payload = {ID: data.ID};
      Object.keys(changed).forEach((field) => {
        const val = changed[field];
        // Не відправляємо маску назад
        if (SENSITIVE_FIELDS.includes(field) && isMasked(val)) return;
        payload[field] = val;
      });

      if ('IsGatewayCardPaymentAllow' in payload) {
        payload.IsGatewayCardPaymentAllow = payload.IsGatewayCardPaymentAllow ? 1 : 0;
      }

      if ('IsPaymentCascade' in payload) {
        payload.IsPaymentCascade = payload.IsPaymentCascade ? 1 : 0;
      }

      try {
        const response = await apiRequest({
          operation: 'Merchant/Mod',
          data: {Params: payload},
        });
        if (response?.data?.ResponseCode === SUCCESS) {
          notify('Сохранено', 'success', 2000);
          onClose();
          onSuccess();
        } else {
          notify(response?.data?.ResponseText || 'Ошибка', 'error', 3000);
        }
      } catch (e) {
        notify('Ошибка сети', 'error', 3000);
      }
    }
  };

  const title = POPUP_TITLES[mode] || '';

  const sensitiveStateRef = useRef({
    Token: {isVisible: false, timerId: null, expiresAtMs: 0},
    CryptoNotifySignKey: {isVisible: false, timerId: null, expiresAtMs: 0},
    PublicKey: {isVisible: false, timerId: null, expiresAtMs: 0},
  });

  const getEditor = useCallback((fieldName) => {
    try {
      return formRef.current?.instance?.().getEditor?.(fieldName) || null;
    } catch (e) {
      return null;
    }
  }, []);

  const setEditorButtonOption = useCallback((editor, buttonName, optionName, value) => {
    if (!editor) return;
    const buttons = editor.option('buttons') || [];
    const idx = buttons.findIndex((b) => b?.name === buttonName);
    if (idx < 0) return;
    editor.option(`buttons[${idx}].options.${optionName}`, value);
  }, []);

  const setTimerHidden = useCallback((editor, hidden) => {
    if (!editor) return;
    const buttons = editor.option('buttons') || [];
    const idx = buttons.findIndex((b) => b?.name === SENSITIVE_TIMER_BUTTON_NAME);
    if (idx < 0) return;
    const baseClass = 'merchant-sensitive-timer';
    const className = hidden ? `${baseClass} merchant-sensitive-timer--hidden` : baseClass;
    editor.option(`buttons[${idx}].options.elementAttr`, {class: className});
  }, []);

  const setEditorTitle = useCallback((editor, title) => {
    if (!editor) return;
    const prev = editor.option('inputAttr') || {};
    editor.option('inputAttr', {...prev, title: title || ''});
  }, []);

  const hideSensitive = useCallback((fieldName) => {
    const state = sensitiveStateRef.current?.[fieldName];
    if (!state) return;

    state.isVisible = false;
    state.expiresAtMs = 0;

    if (state.timerId) {
      clearInterval(state.timerId);
      state.timerId = null;
    }

    const editor = getEditor(fieldName);
    if (!editor) return;

    editor.option('mode', 'password');
    setEditorButtonOption(editor, SENSITIVE_TOGGLE_BUTTON_NAME, 'icon', 'eyeopen');
    setEditorButtonOption(editor, SENSITIVE_TOGGLE_BUTTON_NAME, 'hint', 'Показать');
    setEditorButtonOption(editor, SENSITIVE_TIMER_BUTTON_NAME, 'text', '');
    setTimerHidden(editor, true);
    setEditorTitle(editor, '');
  }, [getEditor, setEditorButtonOption, setEditorTitle, setTimerHidden]);

  const revealSensitive = useCallback((fieldName) => {
    if (!canReveal || isReadOnly) return;

    const state = sensitiveStateRef.current?.[fieldName];
    if (!state) return;

    const editor = getEditor(fieldName);
    if (!editor) return;

    state.isVisible = true;
    state.expiresAtMs = Date.now() + TIMER_SECONDS * 1000;

    editor.option('mode', 'text');
    setEditorButtonOption(editor, SENSITIVE_TOGGLE_BUTTON_NAME, 'icon', 'eyeclose');
    setEditorButtonOption(editor, SENSITIVE_TOGGLE_BUTTON_NAME, 'hint', 'Скрыть');
    setTimerHidden(editor, false);
    setEditorTitle(editor, editor.option('value'));

    if (state.timerId) {
      clearInterval(state.timerId);
      state.timerId = null;
    }

    const tick = () => {
      const remainingMs = state.expiresAtMs - Date.now();
      const seconds = Math.max(0, Math.ceil(remainingMs / 1000));

      const ed = getEditor(fieldName);
      if (ed) {
        setEditorButtonOption(ed, SENSITIVE_TIMER_BUTTON_NAME, 'text', `${String(seconds).padStart(2, '0')}с`);
      }

      if (seconds <= 0) {
        hideSensitive(fieldName);
      }
    };

    tick();
    state.timerId = setInterval(tick, 1000);
  }, [canReveal, getEditor, hideSensitive, isReadOnly, setEditorButtonOption, setEditorTitle, setTimerHidden]);

  const toggleSensitive = useCallback((fieldName) => {
    const state = sensitiveStateRef.current?.[fieldName];
    if (!state) return;
    if (state.isVisible) hideSensitive(fieldName);
    else revealSensitive(fieldName);
  }, [hideSensitive, revealSensitive]);

  useEffect(() => {
    const t = setTimeout(() => {
      Object.keys(sensitiveStateRef.current).forEach((fieldName) => hideSensitive(fieldName));
    }, 0);
    return () => clearTimeout(t);
  }, [hideSensitive]);

  useEffect(() => {
    Object.keys(sensitiveStateRef.current).forEach((fieldName) => hideSensitive(fieldName));
  }, [hideSensitive, mode]);

  useEffect(() => {
    return () => {
      Object.keys(sensitiveStateRef.current).forEach((fieldName) => hideSensitive(fieldName));
    };
  }, [hideSensitive]);

  const getSensitiveEditorOptions = useCallback((fieldName) => {
    if (mode === 'view') return null;

    return {
      mode: 'password',
      readOnly: isReadOnly,
      buttons: [
        {
          name: SENSITIVE_TIMER_BUTTON_NAME,
          location: 'after',
          options: {
            text: '',
            disabled: true,
            stylingMode: 'text',
            elementAttr: {class: 'merchant-sensitive-timer merchant-sensitive-timer--hidden'},
            focusStateEnabled: false,
            hoverStateEnabled: false,
            activeStateEnabled: false,
          },
        },
        {
          name: SENSITIVE_TOGGLE_BUTTON_NAME,
          location: 'after',
          options: {
            icon: 'eyeopen',
            stylingMode: 'text',
            hint: 'Показать',
            disabled: !canReveal || isReadOnly,
            onClick: () => toggleSensitive(fieldName),
          },
        },
      ],
    };
  }, [canReveal, isReadOnly, toggleSensitive]);

  const getTokenEditorOptions = useCallback(() => {
    const base = getSensitiveEditorOptions('Token');
    if (!base) return base;

    const buttons = [...(base.buttons || [])];
    buttons.push({
      name: TOKEN_GENERATE_BUTTON_NAME,
      location: 'after',
      options: {
        text: 'Сгенерировать',
        type: 'default',
        stylingMode: 'contained',
        disabled: isReadOnly,
        onClick: () => {
          if (isReadOnly) return;
          const nextValue = generateTokenValue();
          const form = formRef.current?.instance?.();
          if (form?.updateData) {
            form.updateData('Token', nextValue);
          } else {
            const editor = getEditor('Token');
            editor?.option?.('value', nextValue);
          }
          handleFieldChange('Token', nextValue);
          hideSensitive('Token');
        },
      },
    });

    return {...base, buttons};
  }, [getEditor, getSensitiveEditorOptions, handleFieldChange, hideSensitive, isReadOnly]);

  const getDefaults = async () => {
    const r = await apiRequest({
      operation: 'Merchant/GetDefaultValues',
    });

    if (r?.data?.ResponseCode === '000') {
      const defaultValues = r?.data?.Response ?? {};

      setFormData({
        ...formData,
        ...defaultValues,
      });
    }
  }

  return (
    <Popup
      visible={true}
      onHiding={onClose}
      hideOnOutsideClick={false}
      showCloseButton={true}
      title={title}
      width={960}
      height={'auto'}
      maxHeight={'90vh'}
      container={'#main-content'}
    >
      <div style={{padding: '10px 16px 0'}}>
        <div className={'anti-autocomplete'}>
          <input/>
          <input type={'password'}/>
        </div>
        <div style={{paddingBottom: '10px'}}>
          <Button
            text={'По умолчанию'}
            onClick={getDefaults}
          />
        </div>
        <Form
          ref={formRef}
          formData={formData}
          labelLocation="left"
          colCount={1}
          readOnly={isReadOnly}
          onFieldDataChanged={(e) => {
            // Обробляємо не-sensitive поля через стандартну форму
            if (!e?.dataField) return;
            if (SENSITIVE_FIELDS.includes(e.dataField)) return;

            changedFields.current[e.dataField] = e.value;
          }}
        >
          <SimpleItem
            dataField="ID"
            label={{text: 'ID'}}
            editorOptions={{disabled: mode === 'edit', readOnly: mode === 'view'}}
          >
            {mode === 'add' && <RequiredRule message="ID обязателен"/>}
          </SimpleItem>

          <SimpleItem dataField="Name" label={{text: 'Название'}}>
            {(mode === 'add' || mode === 'edit') && <RequiredRule message="Название обязательно"/>}
          </SimpleItem>

          <SimpleItem
            dataField="Token"
            label={{text: 'Ключ'}}
            editorType="dxTextBox"
            editorOptions={{
              ...getTokenEditorOptions(),
              onValueChanged: (e) => {
                if (isReadOnly) return;
                handleFieldChange('Token', e.value);
              },
            }}
          >
            {mode === 'add' && <RequiredRule/>}
          </SimpleItem>

          <SimpleItem
            dataField="CryptoNotifySignKey"
            label={{text: 'Крипто ключ'}}
            editorType="dxTextBox"
            editorOptions={{
              ...getSensitiveEditorOptions('CryptoNotifySignKey'),
              onValueChanged: (e) => {
                if (isReadOnly) return;
                handleFieldChange('CryptoNotifySignKey', e.value);
              },
            }}
          />

          <SimpleItem
            dataField="PublicKey"
            label={{text: 'Публичный ключ'}}
            editorType="dxTextBox"
            editorOptions={{
              ...getSensitiveEditorOptions('PublicKey'),
              onValueChanged: (e) => {
                if (isReadOnly) return;
                handleFieldChange('PublicKey', e.value);
                if (sensitiveStateRef.current?.PublicKey?.isVisible) {
                  const editor = getEditor('PublicKey');
                  setEditorTitle(editor, e.value);
                }
              },
            }}
          />

          <SimpleItem
            editorType={'dxTextArea'}
            dataField="NotifyUrl"
            label={{text: 'Статус URL'}}
            editorOptions={{
              minHeight: 32,
              maxHeight: 150,
              autoResizeEnabled: true,
              elementAttr: {class: 'merchant-textarea--32'},
            }}
          />

          <SimpleItem
            editorType={'dxTextArea'}
            dataField="IncomeRedirectUrl"
            label={{text: 'Редирект URL'}}
            editorOptions={{
              minHeight: 32,
              maxHeight: 150,
              autoResizeEnabled: true,
              elementAttr: {class: 'merchant-textarea--32'},
            }}
          />

          <SimpleItem
            editorType={'dxTextArea'}
            dataField="CryptoNotifyUrl"
            label={{text: 'Крипто статус URL'}}
            editorOptions={{
              minHeight: 32,
              maxHeight: 150,
              autoResizeEnabled: true,
              elementAttr: {class: 'merchant-textarea--32'},
            }}
          />

          <SimpleItem
            dataField="IsPaymentCascade"
            label={{text: 'Каскадинг платежей'}}
            editorType="dxCheckBox"
            editorOptions={{
              readOnly: isReadOnly,
            }}
          />

          <SimpleItem
            dataField="IsGatewayCardPaymentAllow"
            label={{text: 'Карты: Н2Н Режим'}}
            editorType="dxCheckBox"
            editorOptions={{
              readOnly: isReadOnly,
            }}
          />

          <SimpleItem
            dataField="GatewayCardPaymentSystemTypeID"
            label={{text: 'Карточная система (ID)'}}
            editorType="dxNumberBox"
            editorOptions={{
              readOnly: isReadOnly,
            }}
          />
        </Form>

        {!isReadOnly && (
          <p style={{margin: '0 0 10px', paddingLeft: '160px', fontSize: '11px', color: '#888'}}>
            * Платежная система указывается согласно настроек
          </p>
        )}

        <div style={{display: 'flex', justifyContent: 'flex-end', gap: '8px', padding: '10px 0 14px'}}>
          {mode !== 'view' && (
            <Button
              text="Сохранить"
              type="success"
              onClick={handleSave}
            />
          )}
          <Button
            text="Закрыть"
            onClick={onClose}
          />
        </div>
      </div>
    </Popup>
  );
};

export default MerchantPopup;
